import type { DragEndEvent } from '@dnd-kit/core'
import type { SessionProgressionMode } from '@zeus/index'
import type { PathChapter } from '~/pages/backoffice/admin/sessions/path-chapters'
import type { PathStep, ReadyModule } from '~/pages/backoffice/admin/sessions/path-step'
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import ChapterHeader from '~/pages/backoffice/admin/sessions/components/ChapterHeader'
import PathStageGroup from '~/pages/backoffice/admin/sessions/components/PathStageGroup'
import PathStepRow from '~/pages/backoffice/admin/sessions/components/PathStepRow'
import PrerequisiteFields from '~/pages/backoffice/admin/sessions/components/PrerequisiteFields'
import RetakeFields from '~/pages/backoffice/admin/sessions/components/RetakeFields'
import { addChapter, chaptersAfterRemoval, chapterSegments, removeChapter, renameChapter } from '~/pages/backoffice/admin/sessions/path-chapters'
import { groupIntoStages } from '~/pages/backoffice/admin/sessions/path-stages'
import { createsLoop, newPathStep, prerequisiteOptions, withoutStep } from '~/pages/backoffice/admin/sessions/session-path-form'
import { useDragAnnouncements } from './use-drag-announcements'

type IProps = {
  modules: ReadyModule[]
  value: PathStep[]
  onChange: (steps: PathStep[]) => void
  /** Bornes du parcours, pour signaler les dates incohérentes. */
  startDate: string
  endDate: string
  /** En séquentiel, chaque module attend aussi le précédent : une boucle de prérequis s'en déduit (#80). */
  progressionMode: `${SessionProgressionMode}`
  /** Chapitres du parcours (#75), attachés à des positions de la liste. */
  chapters: PathChapter[]
  onChaptersChange: (chapters: PathChapter[]) => void
}

/**
 * Composition d'un parcours : quels modules, dans quel ordre, à quelle date
 * chacun s'ouvre, et quel module (avec quel score) l'ouvre (#80).
 *
 * L'ordre se règle au glisser-déposer, et la POSITION n'est jamais saisie : elle
 * se déduit de l'ordre de la liste, seule source d'ordre côté API comme ici.
 */
function ModulePathBuilder({ modules, value, onChange, startDate, endDate, progressionMode, chapters, onChaptersChange }: IProps) {
  const { t } = useTranslation()
  const [toAdd, setToAdd] = useState('')

  const byId = new Map(modules.map(m => [m.id, m]))
  const available = modules.filter(m => !value.some(s => s.moduleId === m.id))
  const segments = chapterSegments(value, chapters)

  const dragAnnouncements = useDragAnnouncements(value.map(step => step.moduleId), id => byId.get(id)?.title ?? id)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    const from = value.findIndex(s => s.moduleId === active.id)
    const to = value.findIndex(s => s.moduleId === over.id)
    if (from === -1 || to === -1) return
    onChange(arrayMove(value, from, to))
  }

  const addModule = (moduleId: string) => {
    if (!moduleId || value.some(s => s.moduleId === moduleId)) return
    onChange([...value, newPathStep(moduleId)])
    setToAdd('')
  }

  const updateStep = (moduleId: string, patch: Partial<PathStep>) => {
    onChange(value.map(s => (s.moduleId === moduleId ? { ...s, ...patch } : s)))
  }

  /** Applique une date à tout un palier — « ces trois modules le 15 », en un geste. */
  const setStageDate = (stageSteps: PathStep[], unlockAt: string) => {
    const ids = new Set(stageSteps.map(s => s.moduleId))
    onChange(value.map(s => (ids.has(s.moduleId) ? { ...s, unlockAt } : s)))
  }

  /**
   * Une date hors des bornes du parcours n'est pas refusée ici — l'API ne
   * refuse que celles postérieures à la fin, qui rendraient le module
   * inouvrable. Le reste est signalé sans bloquer : une date antérieure au
   * début est redondante, pas fausse (le verrou effectif est le plus tardif des
   * deux), et une date passée est légitime quand on rattrape une promo lancée.
   */
  const warningFor = (unlockAt: string): string | undefined => {
    if (!unlockAt) return undefined
    if (endDate && unlockAt > endDate) return t('sessions.path.warn.afterEnd')
    if (startDate && unlockAt < startDate) return t('sessions.path.warn.beforeStart')
    return undefined
  }

  /** Une étape et ses réglages : verrou, prérequis, refaire. */
  const renderStep = (step: PathStep) => (
    <PathStepRow
      key={step.moduleId}
      step={step}
      index={value.indexOf(step)}
      title={byId.get(step.moduleId)?.title ?? step.moduleId}
      warning={createsLoop(value, step.moduleId, progressionMode)
        ? t('sessions.path.warn.loop')
        : warningFor(step.unlockAt)}
      onRemove={() => {
        // Retirer une étape re-tranche les chapitres : aucun ne doit rester vide (#75).
        onChaptersChange(chaptersAfterRemoval(chapters, value.indexOf(step), value.length - 1))
        onChange(withoutStep(value, step.moduleId))
      }}
      onDateChange={unlockAt => updateStep(step.moduleId, { unlockAt })}
    >
      <PrerequisiteFields
        step={step}
        options={prerequisiteOptions(value, step.moduleId, progressionMode).map(option => ({
          ...option,
          title: byId.get(option.moduleId)?.title ?? option.moduleId,
          masteryScore: byId.get(option.moduleId)?.masteryScore,
        }))}
        onChange={patch => updateStep(step.moduleId, patch)}
      />
      <RetakeFields step={step} onChange={patch => updateStep(step.moduleId, patch)} />
      {!chapters.some(chapter => chapter.start === value.indexOf(step)) && (
        <div className="w-full pl-9">
          <Button
            type="button"
            variant="link"
            className="h-auto p-0 text-xs"
            onClick={() => onChaptersChange(addChapter(chapters, value.indexOf(step), t('sessions.path.chapter.default')))}
          >
            {t('sessions.path.chapter.start')}
          </Button>
        </div>
      )}
    </PathStepRow>
  )

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <label className="text-sm font-medium">{t('sessions.path.title')}</label>
        <span className="text-xs text-muted-foreground">
          {t('sessions.path.count', { count: value.length })}
        </span>
      </div>

      {value.length === 0
        ? (
            <p className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
              {t('sessions.path.empty')}
            </p>
          )
        : (
            // Pas de `modifiers` pour contraindre le glissé à l'axe vertical :
            // `@dnd-kit/modifiers` n'est pas installé, et la stratégie de liste
            // verticale suffit. Une dépendance de plus pour ce confort-là ne se
            // justifie pas.
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              accessibility={dragAnnouncements}
            >
              <SortableContext
                items={value.map(s => s.moduleId)}
                strategy={verticalListSortingStrategy}
              >
                {/* Chapitres, puis paliers à l'intérieur de chacun : deux
                    regroupements emboîtés, jamais croisés (#75). */}
                <div className="flex flex-col gap-4">
                  {segments.map(segment => (
                    <div key={`segment-${segment.start}`} className="flex flex-col gap-3">
                      {segment.chapter && (
                        <ChapterHeader
                          number={chapters.indexOf(segment.chapter) + 1}
                          title={segment.chapter.title}
                          onRename={title => onChaptersChange(renameChapter(chapters, segment.start, title))}
                          onRemove={() => onChaptersChange(removeChapter(chapters, segment.start))}
                        />
                      )}
                      {groupIntoStages(segment.steps).map(stage => (
                        <PathStageGroup
                          key={`${stage.unlockAt}-${stage.steps[0]!.moduleId}`}
                          stage={stage}
                          onStageDate={setStageDate}
                          renderStep={renderStep}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

      {available.length > 0 && (
        <div className="flex items-center gap-2">
          <select
            value={toAdd}
            onChange={e => setToAdd(e.target.value)}
            className="h-9 flex-1 rounded-md border border-input bg-transparent px-3 text-sm"
            aria-label={t('sessions.path.add')}
          >
            <option value="">{t('sessions.selectModule')}</option>
            {available.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
          </select>
          <Button type="button" variant="outline" disabled={!toAdd} onClick={() => addModule(toAdd)}>
            <Plus className="mr-2 size-4" />
            {t('sessions.path.add')}
          </Button>
        </div>
      )}
    </div>
  )
}

export default ModulePathBuilder
